// Client-side "Audit report signing date" extraction from a signed-FS PDF
// (docs/modules/workflow-fs.md §5). Runs entirely in the browser with pdf.js —
// the file never leaves the user's machine and the server does no work.
//
// Algorithm (text layer path only — no OCR): scan pages front to back in
// batches, treat a page
// as part of the Independent Auditor's Report when it carries a title hint or
// a signature-block anchor, take the first date within the 8 lines after the
// last anchor on the page, and validate it against the FYE THE PDF ITSELF
// declares on its front pages ("for the financial year ended …") — a signing
// date must fall 1–400 days AFTER that FYE. This rejects body-text dates and
// the FYE itself, which both occur in real FS files (e.g. a corporate-info
// page listing the auditors right next to the year-end date). The selected
// engagement plays NO part in the check — comparing against
// the engagement FYE would reject valid dates whenever the user picked an
// unrelated engagement. Validated on 5 real test FS files:
// 5/5 correct, all found within the first 20 pages.

const MONTHS = 'Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|'
  + 'Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?';
const DATE_RE = new RegExp(
  `(?:Dated\\s*[:.,]?\\s*)?\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTHS})\\.?,?\\s+(\\d{4})\\b`, 'i');

// Signature-block anchors, most reliable first (the block always names the
// firm's designation; body text mentions exist too — the FYE window kills those).
const ANCHORS = ['public accountants', 'chartered accountants', 'engagement partner on the audit'];
// Title hints marking a page as belonging to the auditor's report.
const PAGE_HINTS = ["independent auditor", "auditor's report", "auditors' report"];
// "for the financial year ended 15 April 2026" — the FYE the report declares
// about itself, read from the front pages.
const FYE_RE = new RegExp(
  `(?:financial\\s+)?(?:year|period)\\s+ended\\s+(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTHS})\\.?,?\\s+(\\d{4})`, 'i');

const MONTH_NUM = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const MAX_SCAN_PAGES = 60;     // the auditor's report always sits in the front section
const PREVIEW_WIDTH = 1240;    // rendered px width of the hit-page preview (legible when zoomed)
const BATCH_SIZE = 10;         // progress granularity + UI yield point
const MIN_TEXT_CHARS = 50;     // below this a page counts as image-only (scanned)
const TAIL_LINES = 8;          // how far below an anchor the date may sit
const FYE_PAGES = 12;          // how far into the PDF the FYE declaration is looked for
const FYE_WINDOW_DAYS = 400;   // signing date must be 1..400 days after the PDF's own FYE
const SECTION_END_STREAK = 3;  // report pages are contiguous; stop after this many non-report pages

const toIso = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const parseDmy = (day, mon, year) => {
  const m = MONTH_NUM[mon.slice(0, 3).toLowerCase()];
  const d = Number(day); const y = Number(year);
  if (!m || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  return dt.getMonth() === m - 1 && dt.getDate() === d ? dt : null;
};

// Rebuild reading-order lines from pdf.js text items: group by baseline y
// (PDF origin is bottom-left), sort groups top→bottom, items left→right.
// Adjacent items are only separated by a space when there is an actual x-gap:
// pdf.js often splits a word/number into touching items ("15" → "1" + "5"),
// and an unconditional join would turn it into "1 5" and corrupt the date.
const pageLines = (textContent) => {
  const rows = [];
  for (const item of textContent.items) {
    if (!item.str || !item.str.trim()) continue;
    const y = item.transform[5];
    const x = item.transform[4];
    let row = rows.find((r) => Math.abs(r.y - y) <= 2);
    if (!row) { row = { y, items: [] }; rows.push(row); }
    row.items.push({ x, end: x + (item.width || 0), str: item.str });
  }
  rows.sort((a, b) => b.y - a.y);
  return rows.map((r) => {
    r.items.sort((a, b) => a.x - b.x);
    let text = '';
    let prevEnd = null;
    for (const it of r.items) {
      if (prevEnd !== null && it.x - prevEnd > 1) text += ' ';
      text += it.str;
      prevEnd = Math.max(prevEnd ?? it.end, it.end);
    }
    return text;
  });
};

/**
 * Scan `file` (a File/Blob holding a PDF) for the audit report signing date.
 *
 * @param file        the PDF File
 * @param onProgress  ({ scanned, total }) called after each batch
 * @param isCancelled () => bool, polled between pages
 * @returns one of (fye = the FYE the PDF declares about itself, or null;
 *   daysFromFye = signing date minus that FYE in days, when both are known —
 *   the AI-review checklist prints both)
 *   { status: 'found', date: 'YYYY-MM-DD', page, raw, fye, daysFromFye }
 *   { status: 'not_found', fye, rejected: { date, page, raw, daysFromFye } | null }
 *   { status: 'no_text' }        — no readable text layer (scanned copy)
 *   { status: 'unreadable' }     — broken or password-protected PDF
 *   { status: 'cancelled' }
 */
const loadPdfjs = async () => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  // Served straight from the web root by scripts/copy-pdf-worker.js — NOT
  // resolved through webpack, which would babel-transpile the worker into an
  // unloadable file (see that script's header). Absolute path, matching the
  // root-deployment assumption in docs/architecture.md §10.
  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  return pdfjs;
};

export async function extractSigningDate(file, { onProgress, isCancelled } = {}) {
  const pdfjs = await loadPdfjs();

  let doc;
  try {
    doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  } catch (e) {
    return { status: 'unreadable' };
  }

  let fyeDate = null;       // the FYE the PDF declares about itself (front pages)
  const inWindow = (d) => {
    if (!fyeDate) return true;
    const days = (d - fyeDate) / 86400000;
    return days >= 1 && days <= FYE_WINDOW_DAYS;
  };

  const total = Math.min(doc.numPages, MAX_SCAN_PAGES);
  // Every anchor+date candidate, unfiltered — the FYE declaration may only be
  // discovered a few pages after an early false hit (corporate-info pages list
  // the auditors right next to the year-end date), so window filtering happens
  // at the end, against the final fyeDate.
  const candidates = [];    // { page, date: Date, iso, raw, hint }
  let textPages = 0;
  let scanned = 0;
  let sawHintPage = false;
  let nonReportStreak = 0;

  try {
    for (let pageNo = 1; pageNo <= total; pageNo++) {
      if (isCancelled && isCancelled()) return { status: 'cancelled' };

      const page = await doc.getPage(pageNo);
      const lines = pageLines(await page.getTextContent());
      page.cleanup();
      scanned = pageNo;

      const text = lines.join('\n');
      if (text.length >= MIN_TEXT_CHARS) textPages++;
      const low = text.toLowerCase();

      if (!fyeDate && pageNo <= FYE_PAGES) {
        const fm = FYE_RE.exec(text);
        if (fm) fyeDate = parseDmy(fm[1], fm[2], fm[3]);
      }

      const anchorIdx = [];
      lines.forEach((l, i) => {
        const ll = l.toLowerCase();
        if (ANCHORS.some((a) => ll.includes(a))) anchorIdx.push(i);
      });
      const hasHint = PAGE_HINTS.some((h) => low.includes(h));
      const isReportPage = hasHint || anchorIdx.length > 0;

      if (anchorIdx.length > 0) {
        const last = anchorIdx[anchorIdx.length - 1];
        const tail = lines.slice(last, last + 1 + TAIL_LINES).join('\n');
        const m = DATE_RE.exec(tail);
        if (m) {
          const d = parseDmy(m[1], m[2], m[3]);
          if (d) candidates.push({ page: pageNo, date: d, iso: toIso(d), raw: m[0].trim(), hint: hasHint });
        }
      }

      // Early stop: once past a contiguous run of report pages that yielded a
      // window-valid hinted hit, the signing date cannot appear again — the
      // rest is FS content. Only trusted when the PDF's FYE is known; without
      // it an early corporate-info false hit could end the scan prematurely.
      if (isReportPage) {
        sawHintPage = sawHintPage || hasHint;
        nonReportStreak = 0;
      } else if (sawHintPage) {
        nonReportStreak++;
        if (nonReportStreak >= SECTION_END_STREAK && fyeDate
            && candidates.some((c) => c.hint && inWindow(c.date))) break;
      }

      if (pageNo % BATCH_SIZE === 0 || pageNo === total) {
        if (onProgress) onProgress({ scanned: pageNo, total });
        // Yield so React can paint the progress between batches.
        await new Promise((r) => setTimeout(r, 0));
      }
    }
  } finally {
    doc.destroy();
  }

  // Window-filter against the PDF's own FYE, prefer hits on title-hinted
  // pages, and take the last one — the report may span pages and the
  // signature block closes it.
  const fyeIso = fyeDate ? toIso(fyeDate) : null;
  const daysAfterFye = (d) => (fyeDate ? Math.round((d - fyeDate) / 86400000) : null);
  const valid = candidates.filter((c) => inWindow(c.date));
  const hinted = valid.filter((c) => c.hint);
  const pool = hinted.length ? hinted : valid;
  const pick = pool[pool.length - 1];
  if (pick) {
    return {
      status: 'found', date: pick.iso, page: pick.page, raw: pick.raw,
      fye: fyeIso, daysFromFye: daysAfterFye(pick.date),
    };
  }
  if (scanned > 0 && textPages / scanned < 0.3) return { status: 'no_text' };
  // Nothing survived — surface the most informative discarded candidate
  // (hinted pages first, latest first) so the user can judge by eye.
  const dropped = candidates.filter((c) => !inWindow(c.date));
  const droppedHinted = dropped.filter((c) => c.hint);
  const note = (droppedHinted.length ? droppedHinted : dropped).pop();
  return {
    status: 'not_found',
    fye: fyeIso,
    rejected: note
      ? { page: note.page, date: note.iso, raw: note.raw, daysFromFye: daysAfterFye(note.date) }
      : null,
  };
}

/**
 * Render one page of `file` to an image so the user can see the signing date
 * in context — the detected value is only a proposal, and the page it came
 * from is what a reviewer would open to check it. `highlight` is the raw
 * matched text (e.g. "15 April 2026"): the text items that spell it are
 * marked with a translucent box so the eye lands on it at once.
 *
 * Runs on the same client-side pdf.js as the scan; a failure here only
 * costs the preview (the caller keeps the detected date), so it rejects
 * rather than swallowing errors.
 *
 * @returns { blob, width, height }  — a JPEG Blob of the rendered page
 */
export async function renderPdfPage(file, pageNo, { highlight = null } = {}) {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  try {
    const page = await doc.getPage(pageNo);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: PREVIEW_WIDTH / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;

    if (highlight) {
      const boxes = highlightBoxes(await page.getTextContent(), highlight, viewport);
      ctx.fillStyle = 'rgba(250, 204, 21, 0.45)';
      ctx.strokeStyle = 'rgba(202, 138, 4, 0.9)';
      ctx.lineWidth = 2;
      for (const [x1, y1, x2, y2] of boxes) {
        const pad = 3;
        ctx.fillRect(x1 - pad, y1 - pad, (x2 - x1) + pad * 2, (y2 - y1) + pad * 2);
        ctx.strokeRect(x1 - pad, y1 - pad, (x2 - x1) + pad * 2, (y2 - y1) + pad * 2);
      }
    }
    page.cleanup();

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9);
    });
    return { blob, width: canvas.width, height: canvas.height };
  } finally {
    doc.destroy();
  }
}

// Viewport rectangles of the text items that spell `needle` on this page.
// Same row reconstruction as pageLines() (so the join rule matches what the
// scan saw); within the row the needle's character span is mapped back to the
// items that overlap it. Whitespace is compared loosely because the scan
// trimmed and the join may have inserted spaces.
const highlightBoxes = (textContent, needle, viewport) => {
  const norm = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  const target = norm(needle);
  if (!target) return [];
  const rows = [];
  for (const item of textContent.items) {
    if (!item.str || !item.str.trim()) continue;
    const y = item.transform[5];
    const x = item.transform[4];
    let row = rows.find((r) => Math.abs(r.y - y) <= 2);
    if (!row) { row = { y, items: [] }; rows.push(row); }
    row.items.push({ x, end: x + (item.width || 0), item });
  }
  const boxes = [];
  for (const row of rows) {
    row.items.sort((a, b) => a.x - b.x);
    let text = '';
    let prevEnd = null;
    const spans = [];
    for (const it of row.items) {
      if (prevEnd !== null && it.x - prevEnd > 1) text += ' ';
      const start = text.length;
      text += it.item.str;
      spans.push({ start, end: text.length, item: it.item });
      prevEnd = Math.max(prevEnd ?? it.end, it.end);
    }
    const lower = text.toLowerCase();
    // Exact offsets first; if only the whitespace-collapsed form matches, the
    // offsets drift by at most the collapsed run lengths — close enough for a
    // highlight box.
    let from = lower.indexOf(target);
    if (from < 0) from = lower.replace(/\s+/g, ' ').indexOf(target);
    if (from < 0) continue;
    const to = from + target.length;
    for (const sp of spans) {
      if (sp.end <= from || sp.start >= to) continue;
      const { item } = sp;
      const h = item.height || Math.abs(item.transform[3]) || 10;
      const rect = viewport.convertToViewportRectangle([
        item.transform[4], item.transform[5],
        item.transform[4] + (item.width || 0), item.transform[5] + h,
      ]);
      boxes.push([
        Math.min(rect[0], rect[2]), Math.min(rect[1], rect[3]),
        Math.max(rect[0], rect[2]), Math.max(rect[1], rect[3]),
      ]);
    }
    // One box per line: pdf.js splits "15 April 2026" into touching items,
    // and a run of small boxes reads as several hits.
    if (boxes.length) {
      return [[
        Math.min(...boxes.map((b) => b[0])), Math.min(...boxes.map((b) => b[1])),
        Math.max(...boxes.map((b) => b[2])), Math.max(...boxes.map((b) => b[3])),
      ]];
    }
  }
  return boxes;
};

// The first PDF among the selected files, or null (auto-detect needs a PDF —
// .doc/.docx uploads are skipped).
export const firstPdf = (files) =>
  (files || []).find((f) => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name))) || null;
